import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  adminUpdateAppointmentById,
  adminUpdatePrice,
  getDoctorsByConsultationId,
} from "../../../config/service";
import {
  glassDialogPaperProps,
  glassDialogBackdropProps,
  GLASS_PANEL_SX,
  GLASS_FIELD_SURFACE_SX,
  GLASS_DIALOG_TITLE_SX,
} from "./consultationsGlassDialog";

/** Israeli new shekel (₪) */
const SHEKEL = "\u20AA";

const fieldReadonlySx = {
  fontWeight: 600,
  cursor: "pointer",
  borderRadius: 1,
  px: 0.75,
  py: 0.5,
  mx: -0.75,
  my: -0.5,
  width: "fit-content",
  maxWidth: "100%",
  "&:hover": { bgcolor: "rgba(15,23,42,0.06)" },
};

export default function ConsultationsMeetingUpdate({
  consultData,
  updateId,
  updateData,
  handleClose,
  updateModal,
  refetchAllmeetings,
  setUpdateData,
}) {
  const { t } = useTranslation();
  const [editingConsultation, setEditingConsultation] = useState(false);
  const [editingPhysician, setEditingPhysician] = useState(false);
  const [selectedConsultationId, setSelectedConsultationId] = useState("");
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const [doctorList, setDoctorList] = useState([]);
  const [priceEditMode, setPriceEditMode] = useState(false);
  const [priceDraft, setPriceDraft] = useState("");
  const [displayPrice, setDisplayPrice] = useState("");
  const [undoToast, setUndoToast] = useState(null);
  const [pendingUndoAction, setPendingUndoAction] = useState(null);

  const meetingRecord =
    updateData && typeof updateData === "object" && !Array.isArray(updateData)
      ? updateData
      : null;

  const clientName = `${meetingRecord?.clientData?.first_name || ""} ${
    meetingRecord?.clientData?.last_name || ""
  }`.trim();

  const doctorDisplayName = useMemo(() => {
    const currentDoctor = doctorList.find(
      (d) => String(d?.doctorId) === String(selectedDoctorId)
    );
    if (!currentDoctor?.doctorData) return "Unassigned";
    return `${currentDoctor.doctorData.first_name || ""} ${
      currentDoctor.doctorData.last_name || ""
    }`.trim();
  }, [doctorList, selectedDoctorId]);

  const consultationDisplayName = useMemo(() => {
    const selected = consultData?.find(
      (c) => String(c?._id) === String(selectedConsultationId)
    );
    return selected ? selected.eng : meetingRecord?.consultation?.eng || "-";
  }, [consultData, selectedConsultationId, meetingRecord?.consultation?.eng]);

  // Re-sync only when opening the dialog or switching meetings — not on every parent
  // `updateData` refetch (new object reference), which was resetting local state after saves.
  useEffect(() => {
    if (!updateModal || !updateId || !meetingRecord?._id) return;
    setEditingConsultation(false);
    setEditingPhysician(false);
    setPriceEditMode(false);
    setSelectedConsultationId(
      String(meetingRecord?.consultation?._id || meetingRecord?.consultingFieldId || "")
    );
    setSelectedDoctorId(
      meetingRecord?.DoctorId != null ? String(meetingRecord.DoctorId) : ""
    );
    const price = meetingRecord?.meetingPrice ?? "";
    setDisplayPrice(price);
    setPriceDraft(price);
  }, [updateModal, updateId, meetingRecord?._id]);

  useEffect(() => {
    const consultationId =
      selectedConsultationId || meetingRecord?.consultation?._id;
    if (!consultationId) return;
    (async () => {
      try {
        const res = await getDoctorsByConsultationId(consultationId);
        setDoctorList(res?.data?.doctors || []);
      } catch (e) {
        setDoctorList([]);
      }
    })();
  }, [selectedConsultationId, meetingRecord?.consultation?._id]);

  useEffect(() => {
    if (!pendingUndoAction?.armedAt) return;
    const onPointerDown = (e) => {
      const clickedUndoControl = e.target?.closest?.("[data-price-undo='true']");
      if (clickedUndoControl) return;
      if (Date.now() - pendingUndoAction.armedAt < 450) return;
      setPendingUndoAction(null);
    };
    window.addEventListener("pointerdown", onPointerDown, true);
    return () => window.removeEventListener("pointerdown", onPointerDown, true);
  }, [pendingUndoAction]);

  useEffect(() => {
    const onUndoShortcut = (e) => {
      const isUndoCombo =
        (e.ctrlKey || e.metaKey) &&
        !e.shiftKey &&
        String(e.key).toLowerCase() === "z";
      if (!isUndoCombo) return;
      if (!pendingUndoAction?.meetingId) return;
      e.preventDefault();
      handlePriceUndo();
    };
    window.addEventListener("keydown", onUndoShortcut);
    return () => window.removeEventListener("keydown", onUndoShortcut);
  }, [pendingUndoAction]);

  const mergeParentMeeting = (patch) => {
    setUpdateData?.((prev) => {
      const isPlainObject = (v) =>
        v && typeof v === "object" && !Array.isArray(v);
      const base = isPlainObject(prev)
        ? prev
        : isPlainObject(meetingRecord)
          ? meetingRecord
          : {};
      return { ...base, ...patch };
    });
  };

  const getMeetingIdForApi = () => {
    const id = meetingRecord?._id ?? updateId;
    return id != null ? String(id) : "";
  };

  const persistAppointmentProfile = async (overrides = {}) => {
    if (!updateId) {
      toast.error(t("Missing meeting"));
      return;
    }
    const consultingFieldId =
      overrides.consultingFieldId ??
      (selectedConsultationId || meetingRecord?.consultation?._id);
    const doctorId =
      overrides.DoctorId !== undefined
        ? overrides.DoctorId
        : selectedDoctorId || "";
    if (!consultingFieldId) {
      toast.error(t("Missing consultation"));
      return;
    }
    const payload = {
      meetingIds: [String(updateId)],
      consultingFieldId: String(consultingFieldId),
      DoctorId: doctorId === "" ? "" : String(doctorId),
    };
    try {
      const response = await adminUpdateAppointmentById(payload);
      const resData = response?.data;
      if (resData?.partialResponseMeetings?.length) {
        const msg = resData.partialResponseMeetings[0]?.message;
        toast.error(msg || t("Update failed"));
        return;
      }
      if ([200, 201, 202].includes(response.status)) {
        toast.success(t(resData?.message || "Updated"));
        const nextConsult = consultData?.find(
          (c) => String(c._id) === String(consultingFieldId)
        );
        const docRow = doctorList.find(
          (d) => String(d?.doctorId) === String(doctorId || "")
        );
        mergeParentMeeting({
          consultation: nextConsult || meetingRecord?.consultation,
          consultingFieldId,
          DoctorId: doctorId || undefined,
          doctorData: doctorId ? docRow?.doctorData : undefined,
        });
        refetchAllmeetings?.();
        setEditingConsultation(false);
        setEditingPhysician(false);
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.msg;
      toast.error(msg || t("Failed to update consultations details"));
    }
  };

  const handlePriceSave = async () => {
    const previousPrice = displayPrice || "";
    const nextPrice = priceDraft?.trim?.() ?? "";
    if (nextPrice === previousPrice) {
      setPriceEditMode(false);
      return;
    }
    const meetingId = getMeetingIdForApi();
    if (!meetingId) {
      toast.error(t("Missing meeting"));
      return;
    }
    setDisplayPrice(nextPrice);
    setPriceEditMode(false);
    try {
      const response = await adminUpdatePrice({
        meetingId,
        meetingPrice: nextPrice,
      });
      if (![200, 201, 202].includes(response.status)) {
        throw new Error("Unexpected status");
      }
      mergeParentMeeting({ meetingPrice: nextPrice });
      setUndoToast({
        open: true,
        message: `Updated consultation price from ${SHEKEL}\u00A0${previousPrice || "-"} to ${SHEKEL}\u00A0${
          nextPrice || "-"
        }`,
      });
      setPendingUndoAction({
        meetingId,
        previousPrice,
        nextPrice,
        armedAt: Date.now(),
      });
      refetchAllmeetings?.();
    } catch (e) {
      setDisplayPrice(previousPrice);
      setPriceDraft(previousPrice);
      const msg = e?.response?.data?.message || e?.response?.data?.msg;
      toast.error(msg || t("Failed to update price"));
    }
  };

  const handlePriceUndo = async () => {
    if (!pendingUndoAction?.meetingId) return;
    const rollback = pendingUndoAction.previousPrice || "";
    const meetingId = getMeetingIdForApi() || String(pendingUndoAction.meetingId);
    setDisplayPrice(rollback);
    setPriceDraft(rollback);
    setUndoToast(null);
    setPendingUndoAction(null);
    try {
      await adminUpdatePrice({
        meetingId,
        meetingPrice: rollback,
      });
      mergeParentMeeting({ meetingPrice: rollback });
      refetchAllmeetings?.();
    } catch (e) {
      // keep local rollback for user clarity
    }
  };

  return (
    <>
      <Dialog
        open={updateModal}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        dir="ltr"
        PaperProps={glassDialogPaperProps()}
        BackdropProps={glassDialogBackdropProps()}
      >
        <DialogTitle
          sx={[
            GLASS_DIALOG_TITLE_SX,
            { textAlign: "left", color: "rgba(49, 46, 129, 0.96)" },
          ]}
        >
          <Box>
            <Typography fontWeight={700}>Consultation Details</Typography>
            <Typography variant="caption" color="text.secondary">
              Refined profile summary
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ ...GLASS_PANEL_SX, p: 2 }}>
            <Stack spacing={1.5}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 1.2,
                  p: 1.25,
                  ...GLASS_FIELD_SURFACE_SX,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.4 }}>
                    Consultation field
                  </Typography>
                  {editingConsultation ? (
                    <Select
                      fullWidth
                      size="small"
                      autoFocus
                      value={selectedConsultationId}
                      onClose={() => setEditingConsultation(false)}
                      onChange={async (e) => {
                        const nextId = e.target.value;
                        setSelectedConsultationId(nextId);
                        setSelectedDoctorId("");
                        await persistAppointmentProfile({
                          consultingFieldId: nextId,
                          DoctorId: "",
                        });
                      }}
                    >
                      {consultData?.map((c) => (
                        <MenuItem key={c._id} value={String(c._id)}>
                          {c.eng}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <Tooltip title={t("Click to edit")}>
                      <Typography
                        sx={fieldReadonlySx}
                        onClick={() => setEditingConsultation(true)}
                      >
                        {consultationDisplayName}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.4 }}>
                    Physician
                  </Typography>
                  {editingPhysician ? (
                    <Select
                      fullWidth
                      size="small"
                      autoFocus
                      value={selectedDoctorId || ""}
                      onClose={() => setEditingPhysician(false)}
                      onChange={async (e) => {
                        const nextDoctorId = e.target.value;
                        setSelectedDoctorId(nextDoctorId);
                        await persistAppointmentProfile({
                          DoctorId: nextDoctorId,
                        });
                      }}
                    >
                      <MenuItem value="">Unassigned</MenuItem>
                      {doctorList.map((d, idx) => (
                        <MenuItem
                          key={`${d?.doctorId || idx}`}
                          value={d?.doctorId != null ? String(d.doctorId) : ""}
                        >
                          {`${d?.doctorData?.first_name || ""} ${
                            d?.doctorData?.last_name || ""
                          }`.trim() || "Doctor"}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <Tooltip title={t("Click to edit")}>
                      <Typography
                        sx={fieldReadonlySx}
                        onClick={() => setEditingPhysician(true)}
                      >
                        {doctorDisplayName}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 1.2,
                  p: 1.25,
                  ...GLASS_FIELD_SURFACE_SX,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.4 }}>
                    Client name
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>{clientName || "-"}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.4 }}>
                    Client ID
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {meetingRecord?.clientData?.id || "-"}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ p: 1.25, ...GLASS_FIELD_SURFACE_SX }}>
                <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.5 }}>
                  Consultation price
                </Typography>
                {!priceEditMode ? (
                  <Tooltip title={t("Click to edit")}>
                    <Chip
                      label={
                        displayPrice ? `${SHEKEL}\u00A0${displayPrice}` : t("Not set")
                      }
                      clickable
                      onClick={() => setPriceEditMode(true)}
                      sx={{
                        borderRadius: "12px",
                        bgcolor: "rgba(14,165,233,0.12)",
                        color: "#0c4a6e",
                        fontWeight: 600,
                      }}
                    />
                  </Tooltip>
                ) : (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      autoFocus
                      value={priceDraft}
                      onChange={(e) => setPriceDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handlePriceSave();
                        }
                      }}
                      placeholder={t("Enter price")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">{SHEKEL}</InputAdornment>
                        ),
                      }}
                      sx={{ maxWidth: 200 }}
                    />
                    <Tooltip title={t("Save price")}>
                      <IconButton
                        color="success"
                        aria-label="Save consultation price"
                        onClick={handlePriceSave}
                        data-price-undo="true"
                      >
                        <CheckIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("Cancel edit")}>
                      <IconButton
                        color="error"
                        aria-label="Cancel consultation price edit"
                        onClick={() => {
                          setPriceDraft(displayPrice || "");
                          setPriceEditMode(false);
                        }}
                        data-price-undo="true"
                      >
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}
              </Box>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} sx={{ textTransform: "none" }}>
            {t("Close")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(undoToast?.open)}
        autoHideDuration={2600}
        onClose={() => setUndoToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message={undoToast?.message || ""}
        action={
          <Button
            data-price-undo="true"
            size="small"
            color="inherit"
            startIcon={<UndoIcon />}
            onClick={handlePriceUndo}
          >
            {t("Undo")}
          </Button>
        }
      />
    </>
  );
}
